import { accountAccessShowLogin } from 'actions/UiActions'
import BedbankMobileOnlyPriceLabel from 'components/Bedbank/BedbankMobileOnlyPriceLabel'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import WalledContent from 'components/Common/WalledContent'
import IconButton from 'components/Luxkit/Button/IconButton'
import TextButton from 'components/Luxkit/Button/TextButton'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import LinePricetagAltIcon from 'components/Luxkit/Icons/line/LinePricetagAltIcon'
import LineTimesIcon from 'components/Luxkit/Icons/line/LineTimesIcon'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { useAppDispatch } from 'hooks/reduxHooks'
import useBedbankRates from 'hooks/useBedbankRates'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { EmptyObject } from 'lib/object/objectUtils'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import MapViewBedbankPricing from '../MapViewOfferTile/MapViewBedbankPricing'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'

const Root = styled.div`
  position: relative;
  display: grid;
  grid-template-rows: ${rem(140)} 1fr;
  height: 100%;

  ${mediaQueryUp.tablet} {
    grid-template-columns: ${rem(120)} 1fr;
    grid-template-rows: auto;
    background-color: ${props => props.theme.palette.neutral.default.eight};
    border-radius: ${(props) => props.theme.borderRadius.S};
    box-shadow: ${props => props.theme.shadow.flat.large};
    width: ${rem(375)};
  }
`

const StyledIconButton = styled(IconButton)`
  position: absolute;
  top: ${rem(8)};
  left: ${rem(8)};
`

const ImageCarouselContainer = styled.div`
  position: relative;
`

const StyledGroup = styled(Group)`
  width: ${rem(255)};
  padding: ${rem(16)};
`

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
  bestPrice?: number
  duration: number
  onClose?: () => void
  checkInDate?: moment.Moment
  checkOutDate?: moment.Moment
  windowSearch: string
  isSpoofed: boolean
}

function BedbankSearchMapCard(props: Props) {
  const {
    filters = EmptyObject,
    offer,
    bestPrice,
    duration,
    onClose,
    windowSearch,
    isSpoofed,
    checkInDate,
    checkOutDate,
  }: Props = props
  const dispatch = useAppDispatch()
  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const { name, property, images } = offer
  // We only show the first image
  const displayImages = useMemo(() => images.length && isTabletScreenSizeAndUp ? [images[0]] : images, [images, isTabletScreenSizeAndUp])

  const handleClose = useCallback<React.MouseEventHandler<HTMLAnchorElement>>((e) => {
    e.preventDefault()
    onClose?.()
  }, [onClose])

  const { onBedbankSelectDatesPrompt } = useContext(GlobalSearchStateContext)
  const loginHandler = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    e.preventDefault()
    dispatch(accountAccessShowLogin())
  }, [dispatch])

  const onSelectDatesPrompt = useCallback<React.MouseEventHandler<HTMLAnchorElement>>((e) => {
    e.preventDefault()
    onBedbankSelectDatesPrompt?.(offer.id, offer.type, windowSearch)
  }, [onBedbankSelectDatesPrompt, offer.id, offer.type, windowSearch])

  const [{ hotelOnlyRates }] = useBedbankRates(offer.id, filters?.rooms, checkInDate?.format(ISO_DATE_FORMAT), checkOutDate?.format(ISO_DATE_FORMAT))
  const selectedHotelRate = hotelOnlyRates[0]
  const hasMobilePromotion = !!selectedHotelRate?.mobilePromotion
  const bedbankSellPrice = offer.sell?.price
  const bedbankSellDuration = offer.sell?.los
  const price = bestPrice ?? bedbankSellPrice
  const los = duration ?? bedbankSellDuration

  return (
    <Root>
      <ImageCarouselContainer>
        <ImageCarousel
          arrowBehaviour="hidden"
          images={displayImages}
          eagerLoadFirstImage
          topRight={<CSSBreakpoint max="mobile"><BookmarkButton offer={offer}/></CSSBreakpoint>}
        />
        {onClose && (
          <StyledIconButton
            size="small"
            shape="circle"
            kind="primary"
            onClick={handleClose}
            variant="ghost"
          >
            <LineTimesIcon/>
          </StyledIconButton>
        )}
      </ImageCarouselContainer>
      <StyledGroup
        direction="vertical"
        fullWidth
        verticalAlign="space-between"
        gap={8}
      >
        <Group direction="vertical" fullWidth gap={4}>
          <Group direction="horizontal" verticalAlign="start" gap={8}>
            <Group direction="vertical" fullWidth horizontalAlign="space-between">
              {hasMobilePromotion && <BedbankMobileOnlyPriceLabel />}
              <Heading wrap="pre-wrap" variant="heading6">{name}</Heading>
              {isSpoofed && <ProductTypeLabel kind="plain" productType={offer.productType} />}
            </Group>
            {isOfferRatingDisplayable(property.rating) &&
              <OfferRating
                variant="xs"
                rating={property.rating}
                onlyNumber
              />
            }
          </Group>
        </Group>
        <WalledContent
          enforced={offer.walledGarden}
          hidePricing={offer.hidePricing}
          wall={
            <>
              <CaptionTextBlock
                variant="medium"
                weight="bold"
                startIcon={<LinePricetagAltIcon />}
              >
                Members get exclusive rates
              </CaptionTextBlock>
              <TextButton
                kind="secondary"
                onClick={loginHandler}
                size="small"
                fit="full-width"
              >
                Sign up for free
              </TextButton>
            </>
          }
        >
          <div>
            {!price && (
              <TextButton kind="secondary" fit="full-width" onClick={onSelectDatesPrompt}>
                Select dates to view rates
              </TextButton>
            )}
            {price && (
              <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
                <MapViewBedbankPricing
                  filters={filters ?? EmptyObject}
                  offer={offer}
                  price={price}
                  duration={los}
                />
                <CSSBreakpoint max="mobile">
                  <TextButton kind="primary">
                    View offer
                  </TextButton>
                </CSSBreakpoint>
              </Group>
            )}
          </div>
        </WalledContent>
      </StyledGroup>
    </Root>
  )
}

type MappedStateProps = Pick<Props, 'windowSearch' | 'isSpoofed'>

export default connect<MappedStateProps, undefined, Omit<Props, 'windowSearch' | 'isSpoofed'>, App.State>(
  (state) => ({
    windowSearch: state.router.location.search,
    isSpoofed: state.auth.account.isSpoofed,
  }),
)(BedbankSearchMapCard)
